import React, { ChangeEvent, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "../EntityEditor.styles";
import StyledTextField from "../../styled-components/StyledTextField";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import AlertService from "../../../services/notification.service";
import { LatinTextField } from '../../latin-text-field/LatinTextField';
// import { initialErrorEditState } from './EntityAddressBlock';

export type EntityEmailsBlockHandle = {
  reset: () => void;
}

interface EntityEmailsBlockProps  {
  emails?: any,
  onChange?: any,
}

const initialEditModeState = {
  isEdit: false,
  index: -1,
};

const initialEmailState = {
  type: "",
  email: "",
};

type EmailItem = {
  type: string;
  email: string;
}

export const initialErrorState = {
  type: false,
  email: false,
}

const initialEmailDetails = {
  type: "",
  email: "",
  index: -1,
  id: "",
  modified: false,
};

const normalizeEmail = (raw: string) => {
  return raw.trim().toLowerCase();
};

const isDuplicate = (emailNormalize: string, list: Array<EmailItem>, index?: number) => {
  return list.some((item, idx) => {
    if (index !== undefined && idx === index) {
      return false;
    }
    return emailNormalize === normalizeEmail(item.email);
  });
}

const validateEmail = (email: string, list: Array<EmailItem>, index?: number ): string | null => {

  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const emailNormalize: string = normalizeEmail(email);

  if (!emailNormalize) {
    return "Please fill in the field Email.";
  }
  if (!regex.test(emailNormalize)) {
    return "Please enter a valid email address.";
  }
  if (emailNormalize.length > 150) {
    return "Email is too long.";
  }
  if (isDuplicate(emailNormalize, list, index)) {
    return "This email is already on the list.";
  }
  return null;
}


const EntityEmailsBlock = React.forwardRef<EntityEmailsBlockHandle, EntityEmailsBlockProps>(({ emails, onChange }, ref) => {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(initialEditModeState);
  const [entityEmail, setEntityEmail] = useState(initialEmailState);
  const [entityEmailsList, setEntityEmailsList] = useState<EmailItem[]>([]);
  const [editedEmailDetails, setEditedEmailDetails] =
    useState(initialEmailDetails);
  const [emailsForDelete, setEmailForDelete] = useState([]);
  const [errorFields, setErrorFields] = useState(initialErrorState);
  const [errorEditFields, setErrorEditFields] = useState(initialErrorState);
  // const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const canAdd = !validateEmail(entityEmail.email, entityEmailsList );
  // const canEdit = !validateEmail(editedEmailDetails.email, entityEmailsList);

  useEffect(() => {
    setEntityEmailsList(emails);
  }, [dispatch, emails]);

  const handleClearForm = () => {
    /** clear all fields and error statuses **/
    setEditedEmailDetails(initialEmailDetails);
    setEntityEmail(initialEmailState);
    setEditMode(initialEditModeState);
    setEntityEmailsList([]);
    setEmailForDelete([]);
    setErrorFields(initialErrorState);
    setErrorEditFields(initialErrorState);
    setTouched(false);
  };

  const ClearForm = useCallback(() => {
    handleClearForm();
  }, []);
  useImperativeHandle(ref, () => ({
    reset: ClearForm
  }), [ClearForm]);

  const handleFocus = () => {
    setTouched(false);
  }

  const blurHandle = (
    type: string,
    key: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index?: number,
    ) => {

    // setIsFocused(false);
    const errorMsg = validateEmail(event.target.value, entityEmailsList, index);

    if (errorMsg) {
      setTouched(true);
      if (type === "add") {
        setErrorFields((prev) => ({ ...prev, [key]: true }));
      }
      if (type === "edit") {
        setErrorEditFields((prev) => ({ ...prev, [key]: true }));
      }
      AlertService?.warning?.(errorMsg);
      return;
    }
    if (type === "add") {
      setErrorFields((prev) => ({ ...prev, [key]: false }));
      setTouched(false);
    }
    if (type === "edit") {
      setErrorEditFields((prev) => ({ ...prev, [key]: false }));
      setTouched(false);
    }

  };

  const handleChange = (
    _name: string,
    key: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEntityEmail({
      ...entityEmail,
      [key]: event.target.value,
    });
  };

  const handleChangeItem = (fieldName: string, selectedIndex: number, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditedEmailDetails({
      ...editedEmailDetails,
      [fieldName]: event.target.value,
      index: selectedIndex,
      modified: true,
    });
  };

  const handleDeleteItem = (selectedIndex: number) => {
    const newStateForDelete = [];
    entityEmailsList.map((item: any, index) => {
      if (index === selectedIndex && item.id) {
        // @ts-ignore
        return newStateForDelete.push(item);
      } else {
        return item;
      }
    });

    const filteredList = entityEmailsList.filter(
      (_item: any, index) => index !== selectedIndex
    );

    const newState = {
      filteredList,
      newStateForDelete,
    };
    setEntityEmailsList(filteredList);
    setEmailForDelete([...emailsForDelete, ...newStateForDelete]);
    onChange("entity_emails", newState, true);
  };

  const handleSetEditMode = (type: string, selectedIndex: number) => {
    /** managing open/close state for ip item change, save changes by click in ip list **/
    if (type === "open") {
      setErrorEditFields(initialErrorState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      entityEmailsList.forEach((item: any, index) => {
        if (index === selectedIndex) {
          setEditedEmailDetails({
            ...item,
            index: selectedIndex,
            type: item.type,
            email: item.email,
          });
        }
      });
    }
    if (type === "close") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setErrorEditFields(initialErrorState);
    }
    if (type === "edit") {
      const err = validateEmail(editedEmailDetails.email, entityEmailsList, editedEmailDetails.index);
      if (err) {
        setErrorEditFields((prev) => ({...prev, email: true}));
        return;
      }
      const newState = entityEmailsList.map((item: any, index) => {

        if (index === editedEmailDetails.index && index === selectedIndex) {
          return {
            id: editedEmailDetails.id,
            type: editedEmailDetails.type,
            email: editedEmailDetails.email,
            modified: editedEmailDetails.modified,
          };
        } else {
          return item;
        }
      });
      // @ts-ignore
      setEntityEmailsList(newState);
      onChange("entity_emails", newState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setEditedEmailDetails(editedEmailDetails);
      setErrorEditFields(initialErrorState);
    }
  };

  const addNewEmail = () => {
    const errorMsg = validateEmail(entityEmail.email, entityEmailsList);
    if (errorMsg) {
      setTouched(true);
      setErrorFields((prev) => ({ ...prev, email: true }));
    }
    const newState = [
        ...entityEmailsList,
        {
         type: entityEmail.type,
         email: entityEmail.email,
        },
    ];
        // @ts-ignore
    setEntityEmailsList(newState);
    onChange("entity_emails", newState);
    handleClearForm();
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="legend" sx={styles.textField}>
          Emails
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={styles.fieldHolder}>
          <Box sx={styles.fieldBlock}>
            <LatinTextField
              fullWidth
              label="Type"
              type="text"
              name="type"
              errorFields={errorFields.type}
              setErrorFields={setErrorFields}
              margin="dense"
              value={entityEmail?.type}
              onChange={(e) => handleChange("entity_emails", "type", e)}
            />

          {/*  <StyledTextField*/}
          {/*    fullWidth*/}
          {/*    label="Type"*/}
          {/*    margin="dense"*/}
          {/*    onChange={(e) => handleChange("entity_emails", "type", e)}*/}
          {/*    value={entityEmail.type}*/}
          {/*  />*/}
          </Box>
          <Box sx={styles.fieldBlock}>
            <StyledTextField
              fullWidth
              required
              label="Email"
              margin="dense"
              id="email"
              name="email"
              autoComplete="off"
              error={touched && errorFields?.email}
              onFocus={handleFocus}
              onBlur={(e) => {
                blurHandle('add', 'email', e);
              }}
              onChange={(e) => {
                handleChange('entity_emails', 'email', e);
                setTouched(false);
              }}
              value={entityEmail?.email}
            />
          </Box>
        </Box>
        <Button
          type="button"
          disabled={!canAdd}
          sx={styles.button}
          onClick={addNewEmail}
        >
          Add new email
        </Button>
        <Divider sx={{ marginTop: "15px", marginBottom: "15px" }} />
        {entityEmailsList?.length > 0 &&
          entityEmailsList.map(
            (
              item: {
                type: string;
                email: string;
              },
              index
            ) => {
              return editMode.isEdit && editMode.index === index ? (
                <Box sx={styles.editHolder} key={index}>
                  <Typography variant="body2" sx={styles.textField}>
                    Edit current email
                  </Typography>
                  <Box sx={styles.editFieldsBlockHolder}>
                    <Box sx={styles.fieldBlock}>
                      <LatinTextField
                        fullWidth
                        label="Type"
                        type="text"
                        name="type"
                        variant="standard"
                        errorFields={errorEditFields?.type}
                        setErrorFields={setErrorEditFields}
                        margin="dense"
                        value={editedEmailDetails?.type}
                        onChange={(e) => handleChangeItem("type", index, e)}
                      />

                      {/*<TextField*/}
                      {/*  sx={{ marginRight: "15px", width: "100%" }}*/}
                      {/*  label="Type"*/}
                      {/*  variant="standard"*/}
                      {/*  margin="dense"*/}
                      {/*  onChange={(e) => handleChangeItem("type", index, e)}*/}
                      {/*  value={editedEmailDetails.type}*/}
                      {/*/>*/}
                    </Box>
                    <Box sx={styles.fieldBlock}>
                      <StyledTextField
                        fullWidth
                        required
                        label="Email"
                        margin="dense"
                        name="email"
                        variant="standard"
                        error={touched && errorEditFields?.email}
                        // onFocus={handleFocus}
                        onBlur={(e) => {
                          blurHandle('edit', 'email', e, index);
                        }}
                        onChange={(e) => {
                          handleChangeItem("email", index, e);
                          setTouched(false);
                        }}
                        value={editedEmailDetails?.email}
                      />

                      {/*<TextField*/}
                      {/*  sx={{ width: "100%" }}*/}
                      {/*  label="Email"*/}
                      {/*  required*/}
                      {/*  variant="standard"*/}
                      {/*  margin="dense"*/}
                      {/*  onChange={(e) => handleChangeItem("email", index, e)}*/}
                      {/*  value={editedEmailDetails.email}*/}
                      {/*/>*/}
                    </Box>
                  </Box>
                  <Box sx={styles.editButtonGroup}>
                    <IconButton
                      type="button"
                      disabled={!!validateEmail(editedEmailDetails.email, entityEmailsList, index )}
                      onClick={() => {
                        if (!!validateEmail(editedEmailDetails.email, entityEmailsList, index )) return;
                        handleSetEditMode('edit', index);
                      }}
                    >
                      <DoneIcon fontSize={"small"} />
                    </IconButton>
                    <IconButton
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => handleSetEditMode("close", index)}
                    >
                      <CloseIcon fontSize={"small"} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={styles.itemContainer} key={index}>
                  <Box sx={styles.itemHolder}>
                    <Box sx={styles.infoHolder}>
                      <Box sx={styles.typeInfo}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Type:
                        </Typography>
                        <Tooltip
                          title={item?.type}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemDetails} variant={"h6"}>
                            {item?.type}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={styles.info}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Email:
                        </Typography>
                        <Tooltip
                          title={item?.email}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemInfoDetails} variant={"h6"}>
                            {item?.email}
                          </Typography>
                        </Tooltip>

                      </Box>
                    </Box>
                    <Box sx={styles.iconHolder}>
                      <IconButton
                        aria-label="Edit"
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => handleSetEditMode("open", index)}
                      >
                        <EditIcon sx={styles.editIcon} />
                      </IconButton>
                      <IconButton
                        aria-label="Delete"
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => handleDeleteItem(index)}
                      >
                        <DeleteIcon sx={styles.deleteIcon} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            }
          )}
      </AccordionDetails>
    </Accordion>
  );
});

export default EntityEmailsBlock;
